import { CAR2_BWD, CAR2_FWD, CAR4_BWD, CAR4_FWD, FOOT, NODE_CAR_BLOCKED, TRAIL } from './format';
import {
  MAX_CAR_KMH, MAX_WALK_KMH, PARK_S, driveSeconds, offTrailSeconds, walkSeconds,
} from './profile';
import { flatMetres, partial, snap } from './snap';
import { distanceKm } from '../geo';

/*
 * One search for the whole way: drive, park, walk, and the last steps off any
 * path to the target.
 *
 * The graph is searched in two layers -- driving and walking -- and a state
 * is (node, layer). Driving may hand over to walking at any node a walker can
 * leave from (PARK_S), never back, so the drop-off point is whichever node
 * makes the whole journey quickest: it is found, not guessed. A* with an
 * estimate of straight-line distance at the top speed of the state's layer,
 * which never overestimates, so the route found is the quickest there is in
 * the loaded graph.
 *
 * modes: 'car2' (a 4x2: only what profiles.py lets a 4x2 use), 'car4' (a 4x4),
 * 'walk' (no car at all).
 *
 * The start joins the graph on foot from where the person stands: to a way
 * the car may use within START_CAR_M (the car is parked by a road), or to a
 * path within START_FOOT_M (they are already walking). The target is reached
 * from the nearest paths within TARGET_FOOT_M, the last stretch off any path.
 */
export const START_CAR_M = 150;
export const START_FOOT_M = 1000;
export const TARGET_FOOT_M = 3000;
const START_WALK_KMH = 3;

const DRIVE = 0;
const WALK = 1;
const SOURCE = -2;
const TRANSFER = -1;

const carBits = (mode) => (mode === 'car2' ? [CAR2_FWD, CAR2_BWD] : [CAR4_FWD, CAR4_BWD]);

class Heap {
  constructor() { this.f = []; this.s = []; }
  get size() { return this.s.length; }
  push(f, s) {
    const { f: fs, s: ss } = this;
    let i = ss.length;
    fs.push(f); ss.push(s);
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (fs[p] <= f) break;
      fs[i] = fs[p]; ss[i] = ss[p];
      i = p;
    }
    fs[i] = f; ss[i] = s;
  }
  pop() {
    const { f: fs, s: ss } = this;
    const top = ss[0];
    const lastF = fs.pop();
    const lastS = ss.pop();
    if (ss.length) {
      let i = 0;
      const n = ss.length;
      for (;;) {
        let c = 2 * i + 1;
        if (c >= n) break;
        if (c + 1 < n && fs[c + 1] < fs[c]) c += 1;
        if (fs[c] >= lastF) break;
        fs[i] = fs[c]; ss[i] = ss[c];
        i = c;
      }
      fs[i] = lastF; ss[i] = lastS;
    }
    return top;
  }
}

const kmhToMs = (k) => k / 3.6;

/**
 * The quickest way from `start` to `target` ({lat, lng}) for `mode`, or
 * null when there is none in the loaded graph. See summarise() for its shape.
 */
export function planRoute(graph, start, target, mode, opts = {}) {
  graph.build();
  const startCarM = opts.startCarM ?? START_CAR_M;
  const startFootM = opts.startFootM ?? START_FOOT_M;
  const targetFootM = opts.targetFootM ?? TARGET_FOOT_M;
  const n = graph.lat.length;
  const driving = mode === 'car2' || mode === 'car4';
  const [fwd, bwd] = carBits(mode);
  const T = 2 * n;
  const dist = new Float64Array(2 * n + 1).fill(Infinity);
  const prev = new Int32Array(2 * n + 1).fill(-3);
  const via = new Int32Array(2 * n + 1).fill(-3);   // edge index, TRANSFER or SOURCE
  const sourceOf = new Map();                       // state -> start candidate
  const heap = new Heap();
  const tLat = target.lat;
  const tLng = target.lng;
  const hDrive = (u) => (distanceKm(graph.lat[u], graph.lng[u], tLat, tLng) * 1000) / kmhToMs(MAX_CAR_KMH);
  const hWalk = (u) => (distanceKm(graph.lat[u], graph.lng[u], tLat, tLng) * 1000) / kmhToMs(MAX_WALK_KMH);
  const estimate = (s) => (s === T ? 0 : (s & 1) === DRIVE ? hDrive(s >> 1) : hWalk(s >> 1));

  const relax = (state, g, from, edge) => {
    if (g < dist[state]) {
      dist[state] = g;
      prev[state] = from;
      via[state] = edge;
      heap.push(g + estimate(state), state);
      return true;
    }
    return false;
  };

  const startWalk = (m) => m / kmhToMs(START_WALK_KMH);
  // Sources: onto a drivable way (by the car), and onto a path (on foot).
  if (driving) {
    for (const c of snap(graph, start.lat, start.lng, { radiusM: startCarM,
      accept: (e) => (e.flags & (fwd | bwd)) !== 0 })) {
      const e = graph.edges[c.edge];
      const base = startWalk(c.distM);
      if (e.flags & bwd && !(graph.nodeFlags[e.a] & NODE_CAR_BLOCKED)) {
        const s = 2 * e.a + DRIVE;
        if (relax(s, base + driveSeconds(c.alongM, e.cls, e.surf, e.grade, mode), SOURCE, SOURCE)) {
          sourceOf.set(s, { ...c, toward: 'a', layer: DRIVE });
        }
      }
      if (e.flags & fwd && !(graph.nodeFlags[e.b] & NODE_CAR_BLOCKED)) {
        const s = 2 * e.b + DRIVE;
        if (relax(s, base + driveSeconds(e.len - c.alongM, e.cls, e.surf, e.grade, mode), SOURCE, SOURCE)) {
          sourceOf.set(s, { ...c, toward: 'b', layer: DRIVE });
        }
      }
    }
  }
  for (const c of snap(graph, start.lat, start.lng, { radiusM: startFootM, accept: (e) => (e.flags & FOOT) !== 0 })) {
    const e = graph.edges[c.edge];
    const trail = (e.flags & TRAIL) !== 0;
    const base = offTrailSeconds(c.distM);
    for (const [node, metres, toward] of [[e.a, c.alongM, 'a'], [e.b, e.len - c.alongM, 'b']]) {
      const s = 2 * node + WALK;
      if (relax(s, base + walkSeconds(metres, e.cls, e.sac, trail), SOURCE, SOURCE)) {
        sourceOf.set(s, { ...c, toward, layer: WALK });
      }
    }
  }

  // The target, from the paths near it.
  const finish = new Map(); // node -> {cost, candidate, from}
  for (const c of snap(graph, tLat, tLng, { radiusM: targetFootM, accept: (e) => (e.flags & FOOT) !== 0 })) {
    const e = graph.edges[c.edge];
    const trail = (e.flags & TRAIL) !== 0;
    const off = offTrailSeconds(c.distM);
    for (const [node, metres, from] of [[e.a, c.alongM, 'a'], [e.b, e.len - c.alongM, 'b']]) {
      const cost = off + walkSeconds(metres, e.cls, e.sac, trail);
      const known = finish.get(node);
      if (!known || cost < known.cost) finish.set(node, { cost, candidate: c, from });
    }
  }
  if (!finish.size || !heap.size) return null;

  const done = new Uint8Array(2 * n + 1);
  while (heap.size) {
    const s = heap.pop();
    if (done[s]) continue;
    done[s] = 1;
    if (s === T) return summarise(graph, { start, target, mode, dist, prev, via, sourceOf, T, finish });
    const u = s >> 1;
    const layer = s & 1;
    const g = dist[s];
    if (layer === DRIVE) {
      if (graph.hasFoot[u]) relax(2 * u + WALK, g + PARK_S, s, TRANSFER);
      for (let i = graph.adjStart[u]; i < graph.adjStart[u + 1]; i += 1) {
        const k = graph.adj[i];
        const e = graph.edges[k];
        const forward = e.a === u;
        const v = forward ? e.b : e.a;
        if (!(e.flags & (forward ? fwd : bwd)) || graph.nodeFlags[v] & NODE_CAR_BLOCKED) continue;
        relax(2 * v + DRIVE, g + driveSeconds(e.len, e.cls, e.surf, e.grade, mode), s, k);
      }
    } else {
      const end = finish.get(u);
      if (end) relax(T, g + end.cost, s, -4);
      for (let i = graph.adjStart[u]; i < graph.adjStart[u + 1]; i += 1) {
        const k = graph.adj[i];
        const e = graph.edges[k];
        if (!(e.flags & FOOT)) continue;
        const v = e.a === u ? e.b : e.a;
        relax(2 * v + WALK, g + walkSeconds(e.len, e.cls, e.sac, (e.flags & TRAIL) !== 0), s, k);
      }
    }
  }
  return null;
}

function joinPoints(into, pts) {
  for (const p of pts) {
    const last = into[into.length - 1];
    if (!last || last[0] !== p[0] || last[1] !== p[1]) into.push(p);
  }
}

function lengthOf(points) {
  let m = 0;
  for (let i = 1; i < points.length; i += 1) m += flatMetres(points[i - 1][0], points[i - 1][1], points[i][0], points[i][1]);
  return m;
}

/*
 * {
 *   mode, seconds,
 *   drive: {points: [[lat, lng], ...], metres, seconds} | null,
 *   walk:  {points, metres, seconds} | null,
 *   offTrail: {points: [from, target], metres} | null,   the last steps, off any path
 *   approach: {points: [start, onto the first way], metres} | null,
 *   dropOff: {lat, lng} | null,        where the car is left
 *   endOfPaved: {lat, lng} | null,     where a 4x4 leaves what a 4x2 could drive
 *                                      (the drop-off for a 4x2)
 * }
 */
function summarise(graph, { start, target, mode, dist, prev, via, sourceOf, T, finish }) {
  const states = [];
  let s = prev[T];
  const last = s;
  while (s >= 0) {
    states.push(s);
    if (via[s] === SOURCE) break;
    s = prev[s];
  }
  states.reverse();
  const first = states[0];
  const source = sourceOf.get(first);
  const end = finish.get(last >> 1);
  const drive = { points: [], metres: 0, seconds: 0 };
  const walk = { points: [], metres: 0, seconds: 0 };
  let dropOff = null;
  let endOfPaved = null;
  const [fwd2, bwd2] = [CAR2_FWD, CAR2_BWD];

  // Onto the first way: along part of its edge to the first node.
  const firstLeg = source.layer === 0 ? drive : walk;
  joinPoints(firstLeg.points, partial(graph, source, source.toward));
  const firstEdge = graph.edges[source.edge];
  firstLeg.metres += source.toward === 'a' ? source.alongM : firstEdge.len - source.alongM;
  if (source.layer === 0 && mode === 'car4') {
    const ok = source.toward === 'a' ? firstEdge.flags & bwd2 : firstEdge.flags & fwd2;
    if (!ok) endOfPaved = { lat: source.lat, lng: source.lng };
  }

  for (let i = 1; i < states.length; i += 1) {
    const st = states[i];
    const from = states[i - 1];
    const edge = via[st];
    if (edge === TRANSFER) {
      dropOff = { lat: graph.lat[st >> 1], lng: graph.lng[st >> 1] };
      continue;
    }
    const e = graph.edges[edge];
    const forward = e.a === from >> 1;
    let pts = graph.polyline(edge);
    if (!forward) pts = pts.slice().reverse();
    const leg = (st & 1) === 0 ? drive : walk;
    joinPoints(leg.points, pts);
    leg.metres += e.len;
    if ((st & 1) === 0 && mode === 'car4' && !endOfPaved && !(e.flags & (forward ? fwd2 : bwd2))) {
      endOfPaved = { lat: graph.lat[from >> 1], lng: graph.lng[from >> 1] };
    }
  }

  // The last part: along the edge to the point nearest the target, then off any path.
  const toward = end.from;
  const tail = partial(graph, end.candidate, toward).reverse();
  joinPoints(walk.points, tail);
  const tailEdge = graph.edges[end.candidate.edge];
  walk.metres += toward === 'a' ? end.candidate.alongM : tailEdge.len - end.candidate.alongM;

  // Seconds per leg, from the search's own costs.
  // The drive leg's time runs to the moment the car stops (the approach to it included).
  const lastDrive = states.filter((st) => (st & 1) === 0).pop();
  const driveSecs = lastDrive !== undefined ? dist[lastDrive] : 0;
  const total = dist[T];
  const offTrail = end.candidate.distM > 1
    ? { points: [[end.candidate.lat, end.candidate.lng], [target.lat, target.lng]], metres: end.candidate.distM }
    : null;
  const approach = source.distM > 1
    ? { points: [[start.lat, start.lng], [source.lat, source.lng]], metres: source.distM }
    : null;
  const hasDrive = drive.points.length > 1 && lastDrive !== undefined;
  if (hasDrive) {
    drive.seconds = Math.round(driveSecs);
    walk.seconds = Math.round(total - driveSecs);
    if (!dropOff) dropOff = { lat: graph.lat[lastDrive >> 1], lng: graph.lng[lastDrive >> 1] };
    if (!endOfPaved) endOfPaved = dropOff;
    if (mode === 'car2') endOfPaved = dropOff;
  } else {
    walk.seconds = Math.round(total);
  }
  walk.metres = Math.round(walk.metres);
  drive.metres = Math.round(drive.metres);
  return {
    mode,
    seconds: Math.round(total),
    drive: hasDrive ? drive : null,
    walk: walk.points.length > 1 ? walk : null,
    offTrail,
    approach,
    dropOff: hasDrive ? dropOff : null,
    endOfPaved: hasDrive ? endOfPaved : null,
    drawnMetres: Math.round(lengthOf(drive.points) + lengthOf(walk.points)),
  };
}
